import { useDisclosure } from '@chakra-ui/react';
import {
  IAccountRespSchema,
  IGetSubscriptionSchema,
  SubscriptionBillingStatusEnum,
} from '@monetize/types/app';
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MBox,
  MFlex,
  MHStack,
  MPageHeader,
  MPageSearchInput,
} from '../../../components/Monetize';
import { MDataTableActions } from '../../../components/Monetize/DataTable';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { ROUTES } from '../../../constants';
import {
  ISubscriptionWithBillGroup,
  useSubscriptions,
} from '../../../hooks/AccountDetail/useSubscriptions';
import { useACL } from '../../../services/acl/acl';
import { getIsTrulyEmptyList } from '../../../utils';
import { CancelSubscription } from './components/SubscriptionOverview/CancelSubscription';
import {
  SUBSCRIPTION_LIST_CATALOG,
  SubscriptionListColumnContext,
} from './subscriptionList.columns';

export const SubscriptionList = ({
  account,
  billGroupId,
}: {
  account: IAccountRespSchema;
  billGroupId?: string;
}) => {
  const navigate = useNavigate();
  const { id: accountId } = account;
  const tableRef = useRef<DataTableRef>(null);
  const [selectedRowData, setSelectedRowData] =
    useState<IGetSubscriptionSchema>();
  const {
    filters,
    setFilters,
    searchKey,
    searchTerm,
    setSearchTerm,
    onResetFilter,
    listData,
    pager,
    setPager,
    loading,
    totalRecords,
    refetch,
  } = useSubscriptions({ accountId, billGroupId, view: 'basic' });
  const { canDo } = useACL();

  const {
    open: isCancelModalOpen,
    onOpen: onOpenCancelModal,
    onClose: onCloseCancelModal,
  } = useDisclosure();

  const redirectToEdit = (id: string) => {
    navigate(ROUTES.getSubscriptionEditRoute(accountId, id));
  };

  const handleSubscriptionCancel = (rowData: IGetSubscriptionSchema) => {
    setSelectedRowData(rowData);
    onOpenCancelModal();
  };

  const actionBodyTemplate = (
    rowData: ISubscriptionWithBillGroup,
  ): React.ReactNode => {
    const enabled =
      rowData.billingStatus !== SubscriptionBillingStatusEnum.CANCELED &&
      !rowData.quoteOfferingId &&
      canDo([['billing', 'update']]);

    const actions = [
      {
        title: 'Edit',
        enabled,
        action: () => redirectToEdit(rowData.id),
      },
      {
        title: 'Cancel',
        color: 'danger' as const,
        enabled,
        action: () => handleSubscriptionCancel(rowData),
      },
    ];

    return rowData.locked ? null : <MDataTableActions actions={actions} />;
  };

  const columnContext = useMemo<SubscriptionListColumnContext>(
    () => ({ actionBodyTemplate }),
    [actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    SUBSCRIPTION_LIST_CATALOG,
    columnContext,
  );

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: listData?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const filterComponentReset = React.useRef<any>(null);
  const searchComponentReset = React.useRef<any>(null);

  const resetInternalFilters = () => {
    filterComponentReset.current && filterComponentReset.current();
    searchComponentReset.current && searchComponentReset.current();
  };

  return (
    <MBox>
      <MPageHeader title="Subscriptions" hideContent={isTrulyEmptyList}>
        <MFlex>
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Description"
              value={searchTerm || ''}
              onChange={(e: any) => setSearchTerm && setSearchTerm(e)}
              count={totalRecords}
              resetSearch={searchComponentReset}
              mode="alwaysExpanded"
            />
            <RefreshTableButton refetch={refetch} />

            <ExportEntityButton
              entity="subscriptions"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
              getFilename={() => `subscriptions-${accountId}.csv`}
            />
          </MHStack>
        </MFlex>
      </MPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={listData?.content || []}
        isLoading={loading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={listData?.totalElements || 0}
        totalPages={listData?.totalPages}
        pager={pager}
        setPager={setPager}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        resetFilter={() => {
          resetInternalFilters();
          onResetFilter();
        }}
        onRowClick={(row, _tableRow, openInNewWindow) => {
          const routeToNavigate = ROUTES.getSubscriptionOverviewRoute(
            accountId,
            row.id,
          );
          if (openInNewWindow) {
            window.open(routeToNavigate, '_blank');
          } else {
            navigate(routeToNavigate, { state: accountId });
          }
        }}
        emptyProps={{
          mainMessage: 'Looks like there are no subscriptions here.',
        }}
      />

      <CancelSubscription
        isOpen={isCancelModalOpen}
        onClose={(redirect = true) => {
          onCloseCancelModal();
          setSelectedRowData(undefined);
          redirect && navigate(ROUTES.getAccountSubscriptionsRoute(accountId));
        }}
        accountId={accountId}
        billGroupId={selectedRowData?.billGroupId || ''}
        subscriptionId={selectedRowData?.id!}
      />
    </MBox>
  );
};
